import { array, type Decoder, failure, success } from 'io-ts/Decoder';

/**
 * Decodes an array, rejecting an empty one at runtime.
 *
 * Note: the emptiness guarantee is runtime-only — the decoded static type is the plain
 * `A[]`. For the compile-time guarantee, use `AtLeastOne<T>` in `../types/AtLeastOne`.
 *
 * Typing the result as a non-empty tuple was tried and reverted: this decoder backs 13
 * others (`ui.geometry.Path`, the HITLv2 draft/change/page requests, bulk-update, webhook
 * event types, configuration search params), so the tuple propagates to every producer of
 * those values — and producers build them with `.map()`/`.filter()`, which cannot prove
 * non-emptiness. It cost 24+ guards or assertions across the HITLv2 commit plumbing to buy
 * a guarantee the decoder only actually enforces at the boundary.
 */
export const NonEmptyArray = <A>(decoder: Decoder<unknown, A>): Decoder<unknown, A[]> => {
	return {
		decode: (input) => {
			const result = array(decoder).decode(input);

			if (result._tag === 'Right') {
				const value = result.right;
				return value.length > 0 ? success(value) : failure(input, 'Array cannot be empty');
			}

			return result;
		},
	};
};
